import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ChatContext from '../../services/ChatContext';
import ConversationRailV2 from './ConversationRailV2';
import ThreadV2 from './ThreadV2';
import DetailPanelV2 from './DetailPanelV2';
import { ChatIcon } from './icons';
import { Shell, Stage, Empty } from './styles';

const ChatV2 = () => {
  const { conversations, currentConversation, setCurrentConversation } = useContext(ChatContext);
  const [detailOpen, setDetailOpen] = useState(false);
  const { search } = useLocation();
  const requestedId = new URLSearchParams(search).get('conversation');
  const consumedId = useRef(null);

  // Deep link: /messages-v2?conversation=<uid> opens that conversation, so entry
  // points elsewhere (e.g. the chat icon on a participant card) can jump straight
  // into a thread. Carrying the target in the URL rather than in context is what
  // makes this work: the page we navigated away from clears currentConversation in
  // its unmount cleanup, which runs AFTER the click handler set it.
  //
  // Keyed on `conversations` rather than mount-only because the list loads async and
  // is usually still empty here; a freshly created conversation only shows up after
  // the next poll. `consumedId` makes it fire once per id so pressing back (which
  // nulls currentConversation) doesn't immediately re-open the same thread.
  useEffect(() => {
    if (!requestedId || consumedId.current === requestedId) return;
    const conversation = conversations?.[requestedId];
    if (!conversation) return;
    consumedId.current = requestedId;
    setCurrentConversation(conversation);
  }, [requestedId, conversations, setCurrentConversation]);

  // Clear the open conversation when leaving the chat page. Otherwise the
  // ChatManager stays subscribed to this group (subscribedGroupId set), and
  // fetchAllUnreadMessageCount keeps EXCLUDING it from the total — so a new
  // message in that conversation can never light the sidebar dot until a hard
  // refresh. Resetting to null fires the ChatContext cleanup (unsubscribeFromGroup).
  useEffect(() => {
    return () => setCurrentConversation(null);
  }, [setCurrentConversation]);

  const select = (conversation) => {
    setCurrentConversation(conversation);
    setDetailOpen(false);
  };

  return (
    <Shell>
      <ConversationRailV2
        activeId={currentConversation?.groupId}
        onSelect={select}
      />

      <Stage>
        {currentConversation ? (
          <>
            <ThreadV2
              conversation={currentConversation}
              onBack={() => setCurrentConversation(null)}
              onToggleDetail={() => setDetailOpen((v) => !v)}
              detailOpen={detailOpen}
            />
            {detailOpen && (
              <DetailPanelV2
                conversation={currentConversation}
                onClose={() => setDetailOpen(false)}
              />
            )}
          </>
        ) : (
          <Empty>
            <ChatIcon size={64} style={{ opacity: 0.25 }} />
            <div className="title">Your messages</div>
            <div className="sub">Select a conversation from the list to start chatting.</div>
          </Empty>
        )}
      </Stage>
    </Shell>
  );
};

export default ChatV2;
